/**
 * Boot sequence for the public video application.
 *
 * Two things have to be true before the first screen is worth showing: this browser has an
 * anonymous session, and we know what Altamont wants the screens to say. Both are started
 * together, neither is allowed to throw the page away, and whatever happens the app mounts —
 * a person always gets words on a screen, never a blank page.
 */

import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';

import App from './App.vue';
import { router } from './router';
import { ensureSession, isFirebaseConfigured } from './lib/firebase';
import { useConfigStore } from './stores/config';

/** What we tell someone when the app cannot start. Plain, and never their fault. */
const FATAL_MESSAGE =
  'Something went wrong at our end. Please reload the page, and if it happens again, call Altamont HR — a person will help you apply.';

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(router);

const config = useConfigStore(pinia);

app.config.errorHandler = (error: unknown, _instance, info: string): void => {
  console.error(`[altamont] Unhandled error (${info})`, error);
  config.setFatal(FATAL_MESSAGE);
};

async function boot(): Promise<void> {
  if (!isFirebaseConfigured()) {
    // A table beats a sentence here: it shows at a glance whether the .env file was found at all
    // (everything missing) or found and incomplete (some present), which are different problems
    // with different fixes. Names and presence only — never values, since one is an API key and
    // this goes to the browser console.
    const seen = Object.keys(import.meta.env)
      .filter((key) => key.startsWith('VITE_'))
      .sort();
    console.error(
      '[altamont] Firebase is not configured for this build.\n' +
        'VITE_* values are compiled in at BUILD time from `.env` at the repository root.\n' +
        '`.dev.vars` is wrangler\'s file for the API and is never read by Vite.\n' +
        'After editing `.env`, restart the dev server — the old values stay compiled in.\n' +
        'Run `npm run check:config` to see where each value was found.',
    );
    if (seen.length === 0) {
      console.error('[altamont] This build received NO VITE_* values, so `.env` was not found.');
    } else {
      console.table(
        seen.map((key) => ({ variable: key, set: import.meta.env[key] ? 'yes' : 'empty' })),
      );
    }
    config.setFatal(FATAL_MESSAGE);
  }

  const [session] = await Promise.allSettled([ensureSession(), config.load()]);

  if (session.status === 'rejected') {
    console.error('[altamont] Could not start a session for this browser.', session.reason);
    config.setFatal(FATAL_MESSAGE);
  }

  // Resolve the first route before mounting so nobody sees the wrong screen flash past.
  await router.isReady().catch((error: unknown) => {
    console.error('[altamont] The first route could not be resolved.', error);
  });
}

void boot().finally(() => {
  app.mount('#app');
});
